import { useLang } from "../contexts/LanguageContext";
import { Link } from "react-router-dom";
import { ArrowLeft, Mail, MessageCircleHeart, HelpCircle } from "lucide-react";
import { Button } from "../components/ui/button";

const T = {
  fr: {
    back: "Retour à l'accueil",
    title: "Nous contacter",
    subtitle: "Une question, une remarque, un bug ? Écrivez-nous, on lit tout.",
    email_label: "Par email",
    email_body:
      "Le plus simple : écrivez à bonjour@sereno.money. Nous répondons sous 48 h ouvrées.",
    email_cta: "Écrire un email",
    faq_label: "Questions fréquentes",
    faq_body:
      "Avant d'écrire, jetez un œil au glossaire (22 termes financiers en langage simple) et à l'assistant IA disponible dans l'app pour toute question sur l'épargne.",
    assistant_cta: "Assistant IA",
    glossary_cta: "Glossaire",
    privacy_note:
      "Rassurez-vous : vos emails ne servent qu'à vous répondre. Nous ne les partageons avec personne.",
  },
  en: {
    back: "Back to home",
    title: "Contact us",
    subtitle: "A question, feedback, a bug? Write to us — we read everything.",
    email_label: "By email",
    email_body:
      "The simplest way: write to bonjour@sereno.money. We reply within 48 business hours.",
    email_cta: "Write an email",
    faq_label: "Frequently asked",
    faq_body:
      "Before writing, take a look at the glossary (22 financial terms in plain language) and the AI assistant available in the app for any savings question.",
    assistant_cta: "AI Assistant",
    glossary_cta: "Glossary",
    privacy_note:
      "Rest assured: your emails are used only to reply to you. We do not share them with anyone.",
  },
};

export default function Contact() {
  const { lang } = useLang();
  const tr = T[lang] || T.fr;
  return (
    <div className="mx-auto max-w-3xl px-6 py-12" data-testid="contact-page">
      <Link
        to="/"
        data-testid="back-home-link"
        className="mb-6 inline-flex items-center gap-2 text-base text-[#101A42] hover:text-[#A762B1]"
      >
        <ArrowLeft className="h-4 w-4" />
        {tr.back}
      </Link>
      <div className="mb-6 flex items-center gap-3">
        <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-[#A762B1]/15 text-[#A762B1]">
          <MessageCircleHeart className="h-7 w-7" />
        </div>
        <h1 className="font-serif text-4xl font-semibold text-[#101A42] sm:text-5xl">
          {tr.title}
        </h1>
      </div>
      <p className="text-lg leading-relaxed text-[#4A4A4A]">{tr.subtitle}</p>

      <section className="mt-8 rounded-2xl border-2 border-[#A762B1]/40 bg-gradient-to-br from-[#FDF6EE] to-[#FFF7E9] p-6 sm:p-8">
        <div className="flex items-start gap-3">
          <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-[#A762B1]/20 text-[#A762B1]">
            <Mail className="h-6 w-6" />
          </div>
          <div>
            <h2 className="font-serif text-2xl font-semibold text-[#101A42]">
              {tr.email_label}
            </h2>
            <p className="mt-2 text-base leading-relaxed text-[#101A42]">
              {tr.email_body}
            </p>
            <a href="mailto:bonjour@sereno.money" data-testid="contact-mailto">
              <Button className="mt-4 min-h-[52px] rounded-full bg-[#A762B1] px-6 text-lg font-semibold text-white hover:bg-[#B775C0]">
                <Mail className="mr-2 h-5 w-5" />
                {tr.email_cta}
              </Button>
            </a>
          </div>
        </div>
      </section>

      <section className="mt-6 rounded-2xl border-2 border-[#E8DFC7] bg-white p-6">
        <div className="flex items-start gap-3">
          <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-[#A762B1]/15 text-[#8E4799]">
            <HelpCircle className="h-6 w-6" />
          </div>
          <div className="flex-1">
            <h2 className="font-serif text-2xl font-semibold text-[#101A42]">
              {tr.faq_label}
            </h2>
            <p className="mt-2 text-base leading-relaxed text-[#101A42]">
              {tr.faq_body}
            </p>
            <div className="mt-4 flex flex-wrap gap-3">
              <Link to="/assistant">
                <Button
                  variant="outline"
                  className="min-h-[44px] rounded-full border-2 border-[#A762B1] text-base font-semibold text-[#8E4799] hover:bg-[#A762B1] hover:text-white"
                >
                  {tr.assistant_cta}
                </Button>
              </Link>
              <Link to="/glossary">
                <Button
                  variant="outline"
                  className="min-h-[44px] rounded-full border-2 border-[#101A42] text-base font-semibold text-[#101A42] hover:bg-[#101A42] hover:text-white"
                >
                  {tr.glossary_cta}
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      <p className="mt-6 text-sm italic text-[#4A4A4A]" data-testid="privacy-note">
        {tr.privacy_note}
      </p>
    </div>
  );
}
